import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DirectionProvider } from '@radix-ui/react-direction';
import { setDefaultOptions } from 'date-fns';
import { ar as dfAr, de as dfDe, enGB as dfEn, es as dfEs, fr as dfFr } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  DEFAULT_LOCALE_CONFIG, LOCALE_SETTING_KEY, formatCurrency, formatDate, formatLongDate, formatNumber, formatRelative, formatShortDate,
  parseLocaleConfig, resolveDirection, type CurrencyOptions, type LocaleConfig,
} from '@/lib/locale';
import { translate } from '@/lib/i18n';

// One source for the tenant's language / direction / currency. Everything that
// shows an amount, a date or shell text reads from here, so a save in the admin
// screen reaches the whole app through a single react-query cache entry.

interface LocalizationContextType {
  config: LocaleConfig;
  dir: 'ltr' | 'rtl';
  /** Translate an English UI string (falls back to English); `{name}` tokens are filled from params. */
  t: (text: string, params?: Record<string, string | number>) => string;
  /**
   * Translate a value that may or may not be a UI string (a label from a lookup table, a status, a severity).
   * Strings are looked up and returned unchanged when there is no translation; anything else passes through.
   */
  tx: <T>(value: T) => T;
  currency: (amount: number, opts?: CurrencyOptions) => string;
  number: (n: number) => string;
  date: (value: Date | string | number, withTime?: boolean) => string;
  /** "3 days ago" in the tenant's language; '' for null. */
  ago: (value: Date | string | number | null | undefined) => string;
  longDate: (value: Date | string | number) => string;
  shortDate: (value: Date | string | number) => string;
  save: (next: LocaleConfig) => Promise<void>;
  isSaving: boolean;
}

const defaultValue: LocalizationContextType = {
  config: DEFAULT_LOCALE_CONFIG,
  dir: 'ltr',
  t: (text, params) => translate('en', text, params),
  tx: (v) => v,
  currency: (a, o) => formatCurrency(DEFAULT_LOCALE_CONFIG, a, o),
  number: (n) => formatNumber(DEFAULT_LOCALE_CONFIG, n),
  date: (v, time) => formatDate(DEFAULT_LOCALE_CONFIG, v, time),
  ago: (v) => (v ? formatRelative(DEFAULT_LOCALE_CONFIG, v) : ''),
  longDate: (v) => formatLongDate(DEFAULT_LOCALE_CONFIG, v),
  shortDate: (v) => formatShortDate(DEFAULT_LOCALE_CONFIG, v),
  save: async () => {},
  isSaving: false,
};

// Outside the provider (landing, auth, superadmin) everything stays English/LTR/USD.
const LocalizationContext = createContext<LocalizationContextType>(defaultValue);

// date-fns locale per tenant language. Set as the library default so every existing format()/formatDistance()
// call across the app follows the tenant language without touching each call site.
const DATE_FNS_LOCALES = { en: dfEn, ar: dfAr, fr: dfFr, es: dfEs, de: dfDe } as const;

const cacheKey = (tenantId: string) => `masira.locale.${tenantId}`;

// Last-known config per tenant, so an RTL tenant does not flash LTR on reload
// while the settings request is in flight. Purely a first-paint hint.
function readCache(tenantId: string | null): LocaleConfig | undefined {
  if (!tenantId) return undefined;
  try {
    const raw = localStorage.getItem(cacheKey(tenantId));
    return raw ? parseLocaleConfig(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(tenantId: string, cfg: LocaleConfig) {
  try {
    localStorage.setItem(cacheKey(tenantId), JSON.stringify(cfg));
  } catch {
    /* storage unavailable: the hint is optional */
  }
}

export const LocalizationProvider = ({ children }: { children: ReactNode }) => {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['localization', tenantId], [tenantId]);

  const { data } = useQuery({
    queryKey,
    enabled: !!tenantId,
    staleTime: 60_000,
    placeholderData: () => readCache(tenantId),
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('tenant_settings')
        .select('setting_value')
        .eq('tenant_id', tenantId as string)
        .eq('setting_key', LOCALE_SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      const cfg = parseLocaleConfig(row?.setting_value);
      writeCache(tenantId as string, cfg);
      return cfg;
    },
  });
  const config = data ?? DEFAULT_LOCALE_CONFIG;
  const dir = resolveDirection(config);

  // Synchronous on purpose (not an effect): children format dates during this same render.
  useMemo(() => { setDefaultOptions({ locale: DATE_FNS_LOCALES[config.language] }); }, [config.language]);

  // Document-level lang/dir drives CSS logical properties, native form controls
  // and portalled Radix content. Restored on unmount so non-tenant pages
  // (landing, auth) are never left mirrored.
  useEffect(() => {
    const root = document.documentElement;
    const prev = { lang: root.lang, dir: root.dir };
    root.lang = config.language;
    root.dir = dir;
    return () => {
      root.lang = prev.lang || 'en';
      root.dir = prev.dir || 'ltr';
      setDefaultOptions({ locale: undefined });
    };
  }, [config.language, dir]);

  const mutation = useMutation({
    mutationFn: async (next: LocaleConfig) => {
      if (!tenantId) throw new Error('No tenant context');
      // Upsert, not update: a tenant that never saved localisation has no row yet,
      // and an update matching zero rows would "succeed" without saving anything.
      const { error } = await supabase.from('tenant_settings').upsert(
        {
          tenant_id: tenantId,
          setting_key: LOCALE_SETTING_KEY,
          setting_value: { ...next },
          category: 'localization',
          description: 'Tenant language, text direction and currency',
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'tenant_id,setting_key' },
      );
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      if (tenantId) writeCache(tenantId, next);
      queryClient.setQueryData(queryKey, next);
    },
  });

  const save = useCallback(async (next: LocaleConfig) => { await mutation.mutateAsync(next); }, [mutation]);

  const value = useMemo<LocalizationContextType>(() => ({
    config,
    dir,
    t: (text, params) => translate(config.language, text, params),
    tx: <T,>(v: T): T => (typeof v === 'string' ? (translate(config.language, v) as unknown as T) : v),
    currency: (a, o) => formatCurrency(config, a, o),
    number: (n) => formatNumber(config, n),
    date: (v, time) => formatDate(config, v, time),
    ago: (v) => (v ? formatRelative(config, v) : ''),
    longDate: (v) => formatLongDate(config, v),
    shortDate: (v) => formatShortDate(config, v),
    save,
    isSaving: mutation.isPending,
  }), [config, dir, save, mutation.isPending]);

  return (
    <LocalizationContext.Provider value={value}>
      <DirectionProvider dir={dir}>{children}</DirectionProvider>
    </LocalizationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLocalization = () => useContext(LocalizationContext);
